import { z } from 'zod';
import { ErrorTypes } from '../../../utils/constants.js';
import { baseSettingsSchema, baseStructureSchema } from './base.structure.js';
import { blockSchema } from './block.js';

var PlotType = /* @__PURE__ */ ((PlotType2) => {
  PlotType2["Linear"] = "Linear";
  PlotType2["Quadractic"] = "Quadratic";
  PlotType2["Points"] = "Points";
  PlotType2["Exponential"] = "Exponential";
  return PlotType2;
})(PlotType || {});
const pointSchema = z.object({
  loc: z.object({
    x: z.number(),
    y: z.number()
  })
});
const linearSchema = z.object({
  points: z.object({
    a: pointSchema,
    b: pointSchema
  })
});
const quadraticSchema = z.object({
  points: z.object({
    vertex: pointSchema,
    pathPoint: pointSchema
  })
});
const pointsSchema = z.object({
  points: z.array(pointSchema)
});
const exponentialSchema = z.object({
  points: z.object({
    near: pointSchema,
    far: pointSchema
  }),
  asymptote: linearSchema
});
const basePlotSchema = z.object({
  plotId: z.string(),
  equation: z.string().optional()
});
const plotSchema = z.discriminatedUnion("type", [
  basePlotSchema.extend({
    type: z.literal("Linear" /* Linear */),
    data: linearSchema
  }),
  basePlotSchema.extend({
    type: z.literal("Quadratic" /* Quadractic */),
    data: quadraticSchema
  }),
  basePlotSchema.extend({
    type: z.literal("Points" /* Points */),
    data: pointsSchema
  }),
  basePlotSchema.extend({
    type: z.literal("Exponential" /* Exponential */),
    data: exponentialSchema
  })
]);
const graphingSettingsSchema = baseSettingsSchema.extend({
  graphData: z.object({
    type: z.nativeEnum(PlotType).default("Linear" /* Linear */),
    maxPoints: z.number().min(1, {
      message: ErrorTypes.MIN_POINTS
    }).max(10).default(2),
    sizer: z.number().min(1).max(2).default(1)
  })
});
const graphingStructureSchema = baseStructureSchema.extend({
  query: blockSchema.refine((query) => {
    const hasMedia = query.media?.length > 0;
    if (hasMedia) {
      return true;
    }
    if (!query.text) {
      return false;
    }
    const regex = /<[^>]+>/g;
    const cleanedText = query.text.replace(regex, "").trim();
    const hasLatex = query.text.includes("<katex");
    return cleanedText.length > 0 || hasLatex;
  }, {
    message: ErrorTypes.EMPTY_QUERY
  }),
  answer: z.array(z.string()).min(1, {
    message: ErrorTypes.INVALID_ANSWER
  }).max(1, {
    message: ErrorTypes.INVALID_ANSWER
  }),
  marks: z.object({
    correct: z.number().min(0).max(20)
  }).optional(),
  options: z.array(z.any()),
  graphs: z.array(plotSchema).min(1, {
    message: ErrorTypes.EMPTY_GRAPH
  }),
  settings: graphingSettingsSchema
}).refine((data) => data.graphs.findIndex((graph) => graph.plotId === data.answer[0]) > -1);

export { PlotType, graphingSettingsSchema, graphingStructureSchema };
